import React from 'react';
import { motion } from 'framer-motion';
import { Facebook, Instagram, Linkedin, ArrowUp } from 'lucide-react';
import { contactInfo } from '../../data/mock';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const footerLinks = [
    {
      title: 'Navegación',
      links: [
        { name: 'Inicio', href: '#hero' },
        { name: 'Portafolio', href: '#portfolio' },
        { name: 'Servicios', href: '#services' },
        { name: 'Precios', href: '#pricing' },
      ],
    },
    {
      title: 'Servicios',
      links: [
        { name: 'Diseño Web', href: '#services' },
        { name: 'E-Commerce', href: '#services' },
        { name: 'SEO', href: '#services' },
        { name: 'Mantenimiento', href: '#services' },
      ],
    },
    {
      title: 'Contacto',
      links: [
        { name: contactInfo.email, href: `mailto:${contactInfo.email}` },
        { name: `+51 ${contactInfo.phone}`, href: `tel:+51${contactInfo.phone}` },
        { name: contactInfo.location, href: '#contact' },
      ],
    },
  ];

  const socialLinks = [
    { icon: Facebook, href: contactInfo.social.facebook, label: 'Facebook' },
    { icon: Instagram, href: contactInfo.social.instagram, label: 'Instagram' },
    { icon: Linkedin, href: contactInfo.social.linkedin, label: 'LinkedIn' },
  ];

  return (
    <footer className="bg-navy-800 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 mb-12 text-left">
          {/* Brand Column */}
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <a
              href="#hero"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('#hero');
              }}
              className="inline-block mb-4"
            >
              <span className="text-2xl font-bold text-cyan-400 font-display">WEBMASTER</span>
            </a>
            <p className="text-slate-400 mb-6 leading-relaxed max-w-sm">
              Transformamos ideas en experiencias digitales excepcionales. Tu éxito online es nuestra misión.
            </p>
            
            {/* Social Links */}
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-slate-400 hover:bg-cyan-500 hover:text-white transition-all"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </motion.div>

          {/* Link Columns */}
          {footerLinks.map((column, index) => (
            <motion.div
              key={column.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (index + 1) * 0.1 }}
            >
              <h4 className="text-white font-semibold mb-4 text-sm">{column.title}</h4>
              <ul className="space-y-3">
                {column.links.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.href}
                      onClick={(e) => {
                        if (link.href.startsWith('#')) {
                          e.preventDefault();
                          scrollToSection(link.href);
                        }
                      }}
                      className="text-slate-400 hover:text-cyan-400 transition-colors text-sm"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Divider */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Copyright */}
            <p className="text-slate-500 text-sm text-center md:text-left">
              © {new Date().getFullYear()} Webmaster. Todos los derechos reservados.
            </p>

            {/* Back to Top */}
            <motion.button
              onClick={scrollToTop}
              className="flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors text-sm group"
              whileHover={{ y: -2 }}
            >
              <span>Volver arriba</span>
              <ArrowUp className="w-4 h-4 group-hover:-translate-y-1 transition-transform" />
            </motion.button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
